import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { config } from "../config";
import {
  Card,
  StatusPill,
  Muted,
  EmptyState,
} from "./ui";

interface WorkoutSummary {
  workoutId: string;
  ownerId: number;
  name: string | null;
  createdAt: { _seconds: number; _nanoseconds: number } | string;
}

const ListCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Row = styled(Link)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-radius: 18px;
  text-decoration: none;
  color: inherit;
  background: rgba(15, 23, 42, 0.03);
  border: 1px solid transparent;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(99, 102, 241, 0.35);
    background: rgba(99, 102, 241, 0.08);
  }
`;

const WorkoutTitle = styled.div`
  font-weight: 600;
  color: #0f172a;
`;

const WorkoutMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: #475569;
`;

const Timestamp = styled.span`
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  font-size: 0.8rem;
  font-weight: 500;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;


const SyncButton = styled.button`
  border: 1px solid rgba(99, 102, 241, 0.45);
  background: rgba(99, 102, 241, 0.1);
  color: #3730a3;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.5rem 1rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const WorkoutsPage: React.FC = () => {
  const [workouts, setWorkouts] = useState<WorkoutSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);

  const loadWorkouts = useCallback(() => {
    setLoading(true);
    fetch(`${config.REACT_APP_API_BASE_URL}/workouts`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Failed to load workouts (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        setWorkouts(Array.isArray(data.workouts) ? data.workouts : []);
        setError("");
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Could not load workouts. Please check your network and retry.");
        setLoading(false);
      });
  }, []);

  const syncWorkouts = useCallback(() => {
    setIsSyncing(true);
    fetch(`${config.REACT_APP_API_BASE_URL}/workouts/sync`, {
      method: "POST",
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const message = typeof body.error === "string"
            ? body.error
            : `Failed to sync workouts (${res.status})`;
          throw new Error(message);
        }
        return res.json();
      })
      .then(() => {
        setError("");
        loadWorkouts();
      })
      .catch((err) => {
        console.error(err);
        setError("Could not sync workouts from Garmin right now. Please try again.");
      })
      .finally(() => setIsSyncing(false));
  }, [loadWorkouts]);

  useEffect(() => {
    loadWorkouts();
  }, [loadWorkouts]);

  if (loading) {
    return (
      <Card>
        <StatusPill>Loading</StatusPill>
        <Muted>Pulling your recent workouts from Garmin...</Muted>
      </Card>
    );
  }

  if (workouts.length === 0) {
    return (
      <Card>
        <Header>
          {error ? <StatusPill>Error</StatusPill> : <StatusPill>Empty</StatusPill>}
          <SyncButton type="button" onClick={syncWorkouts} disabled={isSyncing}>
            {isSyncing ? "Syncing..." : "Sync Garmin Workouts"}
          </SyncButton>
        </Header>
        {error ? (
          <EmptyState>{error}</EmptyState>
        ) : (
          <EmptyState>
            No workouts yet. Generate a new session and it will appear here as soon as it&rsquo;s pushed to Garmin.
          </EmptyState>
        )}
      </Card>
    );
  }

  return (
    <ListCard>
      <Header>
        <div>
          <StatusPill>Recent</StatusPill>
          <Muted style={{ marginTop: "0.45rem" }}>
            Drill into earlier workouts to confirm the structure before you run.
          </Muted>
        </div>
        <SyncButton type="button" onClick={syncWorkouts} disabled={isSyncing}>
          {isSyncing ? "Syncing..." : "Sync Garmin Workouts"}
        </SyncButton>
      </Header>

      {workouts.map((w) => {
        const created = typeof w.createdAt === "string"
          ? new Date(w.createdAt)
          : new Date(w.createdAt._seconds * 1000);
        return (
          <Row key={w.workoutId} to={`/workouts/${w.workoutId}`}>
            <div>
              <WorkoutTitle>{w.name || "Untitled workout"}</WorkoutTitle>
              <WorkoutMeta>
                <span>ID: {w.workoutId}</span>
                <span>Owner: {w.ownerId}</span>
              </WorkoutMeta>
            </div>
            <Timestamp>{created.toLocaleString()}</Timestamp>
          </Row>
        );
      })}
    </ListCard>
  );
};

export default WorkoutsPage;
